import { BtnPrimary, Button } from '@components/buttons';
import { IcoArrowRight, IcoCheck, IcoX } from '@components/icons';
import { useAuth, useCurrentTenant } from 'client/lib/auth';
import { rpx } from 'client/lib/rpx-client';
import { detectTimezone } from 'shared/dateutil';
import type { PaneProps } from '../../types';
import { WizardPane } from './wizard-pane';
import { StatusPill } from './status-pill';
import { Dispatch, StateUpdater, useState } from 'preact/hooks';
import { AsyncForm, FormGroup, InputLabel } from '@components/async-form';
import { autoFocusSelf } from 'client/utils/autofocus';
import { Password } from '@components/password';
import { StrictPasswordRequirements } from '@course/components/checkout';

function AuthenticatedPane({ onNext }: { onNext: () => void }) {
  return (
    <WizardPane>
      <div class="max-w-sm mx-auto w-full text-center">
        <StatusPill text="Authenticated" theme="green" className="mb-6" />
        <div class="mb-6">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <IcoCheck class="w-8 h-8 text-green-600" />
          </div>

          <h2 class="text-2xl font-bold text-gray-900 mb-3">Ready to continue!</h2>
          <p class="text-gray-600">
            You're already signed in and ready to set up your calendar subscription.
          </p>
        </div>

        <Button
          onClick={onNext}
          class="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-xl font-semibold"
        >
          Continue to Payment
        </Button>
      </div>
    </WizardPane>
  );
}

type AuthMode = 'email' | 'emailsent' | 'emailreturn' | 'signup';
type AuthState = {
  mode: AuthMode;
  email: string;
};

function SignupEmailForm({
  state,
  setState,
}: {
  state: AuthState;
  setState: Dispatch<StateUpdater<AuthState>>;
}) {
  return (
    <AsyncForm
      class="flex flex-col gap-4"
      onSubmit={async () => {
        const result = await rpx.auth.isEmailTaken({ email: state.email });
        if (result.exists) {
          await rpx.auth.calendarForgotPassword({ email: state.email, mode: 'signup' });
        }
        setState((s) => ({ ...s, mode: result.exists ? 'emailsent' : 'signup' }));
      }}
    >
      <FormGroup prop="email">
        <InputLabel>Email</InputLabel>
        <input
          type="email"
          name="email"
          class="ruz-input"
          value={state.email}
          onInput={(e: any) => {
            setState((prev) => ({ ...prev, email: e.target.value, error: '' }));
          }}
          placeholder="you@example.com"
          required
          ref={autoFocusSelf}
          data-private
        />
      </FormGroup>

      <BtnPrimary class="gap-2 items-center p-3 rounded-full text-base">
        Continue
        <IcoArrowRight class="size-4 shrink-0" />
      </BtnPrimary>
    </AsyncForm>
  );
}

function SignupEmailSent({
  state,
  setState,
}: {
  state: AuthState;
  setState: Dispatch<StateUpdater<AuthState>>;
}) {
  return (
    <div class="flex flex-col bg-green-50 p-8 rounded-2xl">
      <h1 class="font-medium text-xl">Email sent!</h1>
      <p>
        We sent a link to "<em>{state.email}</em>". Click the link in your email to continue. Or,{' '}
        <Button onClick={() => setState((s) => ({ ...s, mode: 'email' }))} class="underline">
          go back to sign up
        </Button>{' '}
        to change your email.
      </p>
    </div>
  );
}

function SignupForm({
  state,
  setState,
  onNext,
}: {
  onNext(): void;
  state: AuthState;
  setState: Dispatch<StateUpdater<AuthState>>;
}) {
  const tenant = useCurrentTenant();
  const auth = useAuth();
  const [form, setForm] = useState({
    name: '',
    password: '',
  });
  return (
    <AsyncForm
      class="flex flex-col gap-4"
      onSubmit={async () => {
        await auth.login(
          rpx.auth.registerNewUser({
            email: state.email,
            name: form.name,
            password: form.password,
            timezone: detectTimezone(),
          }),
        );
        onNext();
      }}
    >
      <FormGroup prop="email">
        <InputLabel>Email</InputLabel>
        <Button
          class="flex gap-2 text-left w-full bg-gray-50 rounded-sm border items-center justify-between p-3 py-2"
          onClick={() => setState((s) => ({ ...s, mode: 'email' }))}
        >
          <span>{state.email}</span>
          <IcoX class="size-4 opacity-75" />
        </Button>
      </FormGroup>

      <FormGroup prop="password">
        <InputLabel>Password</InputLabel>
        <Password
          name="password"
          placeholder="password"
          class="ruz-input"
          value={form.password}
          autoFocus
          onInput={(e: any) => setForm((s) => ({ ...s, password: e.target.value }))}
        />
        {tenant.strictPasswords && (
          <section class="text-gray-600 px-4 mt-6 flex flex-col gap-2">
            <p>Note: Passwords should meet the following requirements:</p>
            <StrictPasswordRequirements />
          </section>
        )}
      </FormGroup>
      <FormGroup prop="name">
        <InputLabel>Name</InputLabel>
        <input
          type="text"
          name="name"
          class="ruz-input"
          placeholder="Your name"
          onInput={(e: any) => setForm((s) => ({ ...s, name: e.target.value }))}
          required
        />
      </FormGroup>

      <BtnPrimary class="gap-2 items-center p-3 rounded-full text-base">
        Create your account
        <IcoArrowRight class="size-4 shrink-0" />
      </BtnPrimary>
    </AsyncForm>
  );
}

function SignupPane({ onNext }: { onNext: () => void }) {
  const [state, setState] = useState<AuthState>({
    mode: 'email',
    email: '',
  });

  return (
    <WizardPane>
      <header class="text-center mb-6">
        <StatusPill text="Sign in or create account" theme="indigo" />
      </header>
      {state.mode === 'email' && <SignupEmailForm state={state} setState={setState} />}
      {state.mode === 'emailsent' && <SignupEmailSent state={state} setState={setState} />}
      {state.mode === 'signup' && <SignupForm onNext={onNext} state={state} setState={setState} />}
    </WizardPane>
  );
}

export function AuthStep({ onNext }: PaneProps) {
  const auth = useAuth();

  if (auth.user) {
    return <AuthenticatedPane onNext={onNext} />;
  }

  return <SignupPane onNext={onNext} />;
}
